import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { BookOpen, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import {
  createKnowledgeBaseEntry,
  deleteKnowledgeBaseEntry,
  fetchKnowledgeBaseEntries,
  updateKnowledgeBaseEntry,
  type KnowledgeBaseEntry,
} from '../api'
import { inputClass } from '../styles'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { Skeleton } from './ui/skeleton'
import { Card } from './ui/card'
import { Pagination } from './ui/pagination'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'

interface Props {
  activeSociedadId: string
}

const emptyForm = { faq_key: '', triggers: '', response: '', buttons: '' }
const PAGE_SIZE = 8

// Botones se editan como líneas "id|Título visible" — evita meter un
// sub-formulario repetible para un caso de uso que hoy es secundario.
function parseButtons(raw: string): { id: string; title: string }[] | undefined {
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  if (lines.length === 0) return undefined
  return lines.map((line) => {
    const [id, ...rest] = line.split('|')
    return { id: id.trim(), title: rest.join('|').trim() || id.trim() }
  })
}

function buttonsToText(buttons: KnowledgeBaseEntry['buttons']): string {
  if (!buttons || buttons.length === 0) return ''
  return buttons.map((b) => `${b.id}|${b.title}`).join('\n')
}

function matches(query: string, ...fields: string[]): boolean {
  if (!query.trim()) return true
  const q = query.trim().toLowerCase()
  return fields.some((f) => f.toLowerCase().includes(q))
}

export function KnowledgeBasePanel({ activeSociedadId }: Props) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['knowledge-base', activeSociedadId],
    queryFn: fetchKnowledgeBaseEntries,
  })

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: ['knowledge-base', activeSociedadId] })
  }

  const createMutation = useMutation({
    mutationFn: createKnowledgeBaseEntry,
    onSuccess: () => {
      invalidate()
      closeDialog()
    },
    onError: (err) => handleMutationError(err),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof updateKnowledgeBaseEntry>[1] }) =>
      updateKnowledgeBaseEntry(id, payload),
    onSuccess: () => {
      invalidate()
      closeDialog()
    },
    onError: (err) => handleMutationError(err),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteKnowledgeBaseEntry,
    onSuccess: invalidate,
  })

  function handleMutationError(err: unknown): void {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      setError('Ya existe una entrada con esa clave (faq_key).')
    } else {
      setError('No se pudo guardar la entrada. Revisá los datos e intentá de nuevo.')
    }
  }

  function closeDialog(): void {
    setOpen(false)
    setEditingId(null)
    setForm(emptyForm)
    setError(null)
  }

  function openCreate(): void {
    setEditingId(null)
    setForm(emptyForm)
    setError(null)
    setOpen(true)
  }

  function openEdit(entry: KnowledgeBaseEntry): void {
    setEditingId(entry.id)
    setError(null)
    setForm({
      faq_key: entry.faq_key,
      triggers: entry.triggers.join('\n'),
      response: entry.response,
      buttons: buttonsToText(entry.buttons),
    })
    setOpen(true)
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    setError(null)
    const payload = {
      faq_key: form.faq_key,
      triggers: form.triggers.split('\n').map((t) => t.trim()).filter(Boolean),
      response: form.response,
      buttons: parseButtons(form.buttons),
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const filtered = useMemo(
    () => (query.data ?? []).filter((e) => matches(search, e.faq_key, ...e.triggers)),
    [query.data, search],
  )
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <Card className="overflow-hidden py-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <BookOpen className="size-4" />
            Preguntas frecuentes
          </h2>
          <p className="text-xs text-muted-foreground">El bot responde estas sin usar IA.</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por clave o frase…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-56 pl-8"
            />
          </div>

          <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : closeDialog())}>
            <DialogTrigger asChild>
              <Button onClick={openCreate}>
                <Plus className="size-4" />
                Nueva entrada
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle>{editingId ? 'Editar entrada de FAQ' : 'Nueva entrada de FAQ'}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="faq-key">Clave (faq_key)</Label>
                  <Input
                    id="faq-key"
                    required
                    placeholder="ej. horario_atencion"
                    value={form.faq_key}
                    onChange={(e) => setForm({ ...form, faq_key: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="faq-triggers">Frases que la disparan</Label>
                  <textarea
                    id="faq-triggers"
                    required
                    rows={3}
                    placeholder="Una por línea"
                    value={form.triggers}
                    onChange={(e) => setForm({ ...form, triggers: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="faq-response">Respuesta</Label>
                  <textarea
                    id="faq-response"
                    required
                    rows={4}
                    placeholder="Respuesta"
                    value={form.response}
                    onChange={(e) => setForm({ ...form, response: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="faq-buttons">Botones opcionales</Label>
                  <textarea
                    id="faq-buttons"
                    rows={2}
                    placeholder="Uno por línea: id|Título"
                    value={form.buttons}
                    onChange={(e) => setForm({ ...form, buttons: e.target.value })}
                    className={inputClass}
                  />
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <DialogFooter>
                  <Button type="submit" disabled={isSubmitting}>
                    {editingId ? <Pencil className="size-4" /> : <Plus className="size-4" />}
                    {editingId ? 'Guardar cambios' : 'Agregar entrada'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {query.isLoading && (
        <div className="flex flex-col gap-3 p-5">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-5/6" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      )}
      {query.isError && <p className="p-5 text-sm text-destructive">No se pudo cargar la base de conocimiento.</p>}
      {query.data && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
          <BookOpen className="size-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            {search ? 'Ninguna entrada coincide con la búsqueda.' : 'Todavía no hay entradas de FAQ.'}
          </p>
        </div>
      )}
      {paged.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Clave</TableHead>
                <TableHead>Triggers</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{entry.faq_key}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1" title={entry.triggers.join(', ')}>
                      {entry.triggers.slice(0, 2).map((t) => (
                        <Badge key={t} variant="secondary" className="font-normal">
                          {t}
                        </Badge>
                      ))}
                      {entry.triggers.length > 2 && (
                        <Badge variant="outline" className="font-normal">
                          +{entry.triggers.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={entry.is_active ? 'outline' : 'destructive'}>
                      {entry.is_active ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(entry)}>
                        <Pencil className="size-3.5" />
                        Editar
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title="Borrar"
                        onClick={() => deleteMutation.mutate(entry.id)}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={page} pageCount={pageCount} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}
    </Card>
  )
}
